import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity, Flame, TrendingUp, Trophy, CalendarRange } from "lucide-react";
import { api } from "../lib/api";
import { ACTIVITY_CATEGORIES, ACTIVITY_INDEX } from "../data/activities";

const CAT_BY_ID = ACTIVITY_CATEGORIES.reduce((acc, c) => { acc[c.id] = c; return acc; }, {});

function formatRange(startIso, endIso) {
  try {
    const start = new Date(startIso);
    const end = new Date(endIso);
    end.setDate(end.getDate() - 1); // endIso is exclusive Monday of next week
    const opts = { month: "short", day: "numeric" };
    return `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`;
  } catch {
    return "This week";
  }
}

/**
 * Weekly Activity Report — calendar week (Mon–Sun, UTC) aggregation of all
 * logged activity completions for the signed-in user.
 *
 * Variants:
 *  - `compact` (default false): full detail card with category & day breakdowns
 *  - `compact`=true: a tighter summary suitable for nesting in other layouts
 */
export default function WeeklyActivityReport({ compact = false }) {
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/activity-completions/weekly-report")
      .then((r) => { if (!cancelled) setReport(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.message || "Failed to load"); });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <div className="bps-card p-4 text-sm text-[#7C8FA8]" data-testid="weekly-report-error">
        Weekly report unavailable. {error}
      </div>
    );
  }
  if (!report) {
    return (
      <div className="bps-card p-4" data-testid="weekly-report-loading">
        <div className="h-3 w-32 bg-white/5 rounded animate-pulse mb-3" />
        <div className="h-2 w-full bg-white/5 rounded animate-pulse" />
      </div>
    );
  }

  const { total, by_category, by_day, top_activities, most_active_day, streak_days, daily_average } = report;
  const maxDay = Math.max(1, ...by_day.map((d) => d.count));
  const maxCat = Math.max(1, ...by_category.map((c) => c.count));

  return (
    <div data-testid="weekly-activity-report" className="space-y-4">
      {/* Header */}
      <div
        className="relative overflow-hidden rounded-3xl border p-5"
        style={{
          borderColor: "#22D3EE55",
          background: "linear-gradient(160deg, rgba(34,211,238,0.16) 0%, rgba(245,158,11,0.06) 50%, rgba(10,21,48,0.9) 100%)",
          boxShadow: "0 20px 50px rgba(34,211,238,0.18)",
        }}
        data-testid="weekly-report-header"
      >
        <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-2xl" style={{ background: "radial-gradient(circle, rgba(34,211,238,0.45), transparent 70%)" }} />
        <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-2xl" style={{ background: "radial-gradient(circle, rgba(245,158,11,0.25), transparent 70%)" }} />

        <div className="relative flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#22D3EE]/18 border border-[#22D3EE]/55 flex items-center justify-center shrink-0">
            <CalendarRange size={20} className="text-[#22D3EE]" strokeWidth={1.7} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="bps-eyebrow text-[#22D3EE]">Weekly activity report</div>
            <div className="font-display text-lg text-white leading-snug mt-0.5" data-testid="weekly-report-range">
              {formatRange(report.week_start, report.week_end)}
            </div>
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#7C8FA8] mt-0.5">
              Mon — Sun · UTC
            </div>
          </div>
        </div>

        {/* Stat tiles */}
        <div className="relative mt-5 grid grid-cols-3 gap-2">
          <StatTile icon={Activity} accent="#22D3EE" label="Logged" value={total} testid="stat-total" />
          <StatTile icon={Flame} accent="#F59E0B" label="Day streak" value={streak_days} testid="stat-streak" />
          <StatTile icon={TrendingUp} accent="#10B981" label="Daily avg" value={daily_average} testid="stat-avg" />
        </div>

        {most_active_day && (
          <div className="relative mt-4 flex items-center gap-2 text-xs text-white/80" data-testid="most-active-day">
            <Trophy size={13} className="text-[#F59E0B]" />
            Most active day:&nbsp;<span className="text-white font-semibold">{most_active_day}</span>
          </div>
        )}
      </div>

      {/* By day */}
      <div className="bps-card p-4" data-testid="report-by-day">
        <div className="bps-eyebrow text-[#7C8FA8] mb-3">By day</div>
        <div className="flex items-stretch gap-2 h-36">
          {by_day.map((d, i) => {
            const heightPct = (d.count / maxDay) * 100;
            const isToday = d.is_today;
            return (
              <div key={d.day} className="flex-1 h-full flex flex-col items-center gap-1.5" data-testid={`day-bar-${d.day.toLowerCase()}`}>
                <div className="text-[10px] font-mono font-semibold" style={{ color: d.count > 0 ? (isToday ? "#22D3EE" : "#FFF") : "#7C8FA8" }}>
                  {d.count}
                </div>
                <div className="w-full flex-1 flex items-end">
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${Math.max(heightPct, d.count > 0 ? 8 : 2)}%` }}
                    transition={{ duration: 0.7, ease: "easeOut", delay: i * 0.05 }}
                    className="w-full rounded-t-md"
                    style={{
                      background: d.count > 0
                        ? `linear-gradient(180deg, ${isToday ? "#22D3EE" : "#1E8FA8"} 0%, ${isToday ? "#0EA5BA" : "#0B5A6B"} 100%)`
                        : "rgba(255,255,255,0.04)",
                      boxShadow: d.count > 0 ? `0 0 12px ${isToday ? "#22D3EE99" : "#1E8FA855"}` : "none",
                    }}
                  />
                </div>
                <div className={`text-[10px] font-mono uppercase tracking-[0.16em] ${isToday ? "text-[#22D3EE]" : "text-[#7C8FA8]"}`}>
                  {d.day}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* By category */}
      {by_category.length > 0 && (
        <div className="bps-card p-4" data-testid="report-by-category">
          <div className="bps-eyebrow text-[#7C8FA8] mb-3">By category</div>
          <div className="space-y-2.5">
            {by_category.map((c) => {
              const cat = CAT_BY_ID[c.category] || { label: c.category, emoji: "•", accent: "#22D3EE" };
              const widthPct = Math.max(8, (c.count / maxCat) * 100);
              return (
                <div key={c.category} className="flex items-center gap-3" data-testid={`report-cat-${c.category}`}>
                  <span className="text-sm leading-none w-5 text-center" aria-hidden>{cat.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-white/90 truncate">{cat.label}</span>
                      <span className="font-mono text-xs font-semibold" style={{ color: cat.accent }}>{c.count}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${widthPct}%` }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        className="h-full rounded-full"
                        style={{ background: cat.accent, boxShadow: `0 0 8px ${cat.accent}99` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top activities */}
      {!compact && top_activities && top_activities.length > 0 && (
        <div className="bps-card p-4" data-testid="report-top-activities">
          <div className="bps-eyebrow text-[#7C8FA8] mb-3">Top activities</div>
          <ul className="space-y-2">
            {top_activities.map((a, i) => {
              const meta = ACTIVITY_INDEX[a.activity_id];
              const accent = meta?.accent || "#22D3EE";
              const Icon = meta?.icon;
              const displayLabel = meta?.label || a.label || a.activity_id;
              return (
                <li key={a.activity_id} className="flex items-center gap-3" data-testid={`top-activity-${a.activity_id}`}>
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-mono font-bold shrink-0"
                    style={{
                      background: i === 0 ? `${accent}33` : "rgba(255,255,255,0.04)",
                      border: `1px solid ${i === 0 ? accent + "AA" : "rgba(255,255,255,0.08)"}`,
                      color: i === 0 ? accent : "#9AAEC6",
                    }}
                  >
                    {i + 1}
                  </div>
                  {Icon && <Icon size={14} className="text-[#9AAEC6] shrink-0" strokeWidth={1.6} />}
                  <span className="flex-1 text-sm text-white/90 truncate">{displayLabel}</span>
                  <span className="font-mono text-xs font-semibold" style={{ color: accent }}>×{a.count}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {total === 0 && (
        <div className="bps-card p-5 text-center" data-testid="report-empty">
          <div className="bps-eyebrow text-[#7C8FA8]">Nothing logged yet</div>
          <p className="text-sm text-white/80 mt-2">
            Log an activity to start seeing your weekly pattern.
          </p>
        </div>
      )}
    </div>
  );
}

function StatTile({ icon: Icon, accent, label, value, testid }) {
  return (
    <div
      className="rounded-2xl border p-3 flex flex-col items-start gap-1"
      style={{
        borderColor: `${accent}40`,
        background: `linear-gradient(160deg, ${accent}1A 0%, rgba(10,21,48,0.6) 70%)`,
      }}
      data-testid={testid}
    >
      <Icon size={14} style={{ color: accent }} strokeWidth={1.8} />
      <div className="font-display text-xl text-white leading-none">{value}</div>
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#7C8FA8]">{label}</div>
    </div>
  );
}
